import React, { useState } from 'react';
import { TabContent, TabPane } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import EmptyState from 'components/EmptyState/EmptyState';
import { NSNavTabs } from 'bricks';
import { IProForma } from 'queries/ProForma/useGetProFormasByDeal';
import { IUploadedProForma } from 'types/db/UploadedProForma';
import { ProFormaSummary, UploadedProFormaSummary } from 'views/ProFormaTable/hooks/useGetDealsWithProFormas';
import { AUTOMATED_PRO_FORMA, MANUAL_SPREADSHEET } from '../../constants';
import DealAutomatedTable from '../DealAutomatedTable/DealAutomatedTable';
import ManualSpreadsheetTable from '../ManualSpreadsheetTable/ManualSpreadsheetTable';

export interface IProFormaTableTabsProps {
    proFormas: IProForma[] | ProFormaSummary[];
    uploadedProFormas: IUploadedProForma[] | UploadedProFormaSummary[];
    isLoadingProFormas?: boolean;
    isLoadingUploadedProFormas?: boolean;
    dealId?: number;
    emptyStateSubtitle?: string;
    isAllDealsProFormasPage?: boolean;
}

const ProFormaTableTabs = ({
    proFormas,
    uploadedProFormas,
    isLoadingProFormas = false,
    isLoadingUploadedProFormas = false,
    dealId,
    emptyStateSubtitle,
    isAllDealsProFormasPage = false,
}: IProFormaTableTabsProps) => {
    const [activeTabName, setActiveTabName] = useState(AUTOMATED_PRO_FORMA);

    const proFormaNavTabs: string[] = [AUTOMATED_PRO_FORMA, MANUAL_SPREADSHEET];

    const getHeapClassName = (navName: string) => ({
        [AUTOMATED_PRO_FORMA]: 'heap-deal-pro-forma-home-deal-automated',
        [MANUAL_SPREADSHEET]: 'heap-deal-pro-forma-home-manual-spreadsheet',
    }[navName] || '');

    const hasAnyProFormas = proFormas?.length > 0 || uploadedProFormas?.length > 0;
    const shouldShowTables = isAllDealsProFormasPage || hasAnyProFormas;

    return (
        <>
            <RenderIf isTrue={!hasAnyProFormas && !isAllDealsProFormasPage}>
                <EmptyState
                    title="No pro formas created yet!"
                    subtitle={emptyStateSubtitle}
                    className="mt-3 vh-100"
                />
            </RenderIf>

            <RenderIf isTrue={shouldShowTables}>
                <NSNavTabs
                    containerClassName="mb-0 text-dark d-flex justify-content-start"
                    navItems={proFormaNavTabs.map((navName, index) => ({
                        callback: () => setActiveTabName(navName),
                        name: navName,
                        isActive: activeTabName === navName,
                        id: `pro-forma-tabbed-view-nav-tab-${index}`,
                        className: `text-dark text-nowrap px-4 ${getHeapClassName(navName)}`,
                    }))}
                />

                <TabContent activeTab={activeTabName}>
                    <TabPane tabId={AUTOMATED_PRO_FORMA}>
                        <RenderIf isTrue={activeTabName === AUTOMATED_PRO_FORMA}>
                            <DealAutomatedTable
                                proFormas={proFormas as IProForma[]}
                                isLoading={isLoadingProFormas}
                                dealId={dealId}
                                isAllDealsProFormasPage={isAllDealsProFormasPage}
                            />
                        </RenderIf>
                    </TabPane>
                    <TabPane tabId={MANUAL_SPREADSHEET}>
                        <RenderIf isTrue={activeTabName === MANUAL_SPREADSHEET}>
                            <ManualSpreadsheetTable
                                uploadedProFormas={uploadedProFormas as IUploadedProForma[]}
                                isLoading={isLoadingUploadedProFormas}
                                dealId={dealId}
                                isAllDealsProFormasPage={isAllDealsProFormasPage}
                            />
                        </RenderIf>
                    </TabPane>
                </TabContent>
            </RenderIf>
        </>
    );
};

export default ProFormaTableTabs;
